/**
 * ARCreditNoteWorkspace.jsx
 * ERP-FIN-02 Gate AR-03: Customer Credit Notes and Refunds Workspace
 *
 * Capabilities:
 *   ar.credit_note.view   — view list
 *   ar.credit_note.create — create new credit note
 *   ar.refund.request     — create refund request
 *
 * Route: /ar/credit-notes
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { arCreditNoteApi } from "../services/api";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "DRAFT", label: "Draft" },
  { key: "APPROVED", label: "Approved" },
  { key: "ISSUED", label: "Issued" },
  { key: "PARTIALLY_APPLIED", label: "Partially Applied" },
  { key: "FULLY_APPLIED", label: "Fully Applied" },
  { key: "VOID", label: "Void" },
  { key: "REJECTED", label: "Rejected" },
];

const STATUS_COLORS = {
  DRAFT:             { bg: "#F3F4F6", text: "#374151" },
  APPROVED:          { bg: "#DBEAFE", text: "#1D4ED8" },
  ISSUED:            { bg: "#D1FAE5", text: "#065F46" },
  PARTIALLY_APPLIED: { bg: "#FEF3C7", text: "#92400E" },
  FULLY_APPLIED:     { bg: "#D1FAE5", text: "#065F46" },
  VOID:              { bg: "#F3F4F6", text: "#6B7280" },
  REJECTED:          { bg: "#FEE2E2", text: "#991B1B" },
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#374151" };
  return (
    <span style={{
      background: c.bg, color: c.text, borderRadius: "12px",
      padding: "2px 10px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function fmtAmt(minor, currency = "INR") {
  const v = (minor || 0) / 100;
  return currency === "INR"
    ? `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : `${currency} ${v.toFixed(2)}`;
}

function fmtDate(d) {
  if (!d) return "—";
  return d.slice(0, 10);
}

export default function ARCreditNoteWorkspace() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [activeTab,   setActiveTab]   = useState("");
  const [creditNotes, setCreditNotes] = useState([]);
  const [counts,      setCounts]      = useState({});
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [showCreate,  setShowCreate]  = useState(false);

  const canCreate = user?.capabilities?.includes("ar.credit_note.create");
  const canRefund = user?.capabilities?.includes("ar.refund.request");

  const loadCreditNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 1, page_size: 50 };
      if (activeTab) params.status = activeTab;
      const res = await arCreditNoteApi.list(params);
      setCreditNotes(res.data.credit_notes || []);
      setCounts(res.data.counts || {});
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load credit notes");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadCreditNotes(); }, [loadCreditNotes]);

  return (
    <div data-testid="ar-credit-note-workspace" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#111827", margin: 0 }}>Customer Credit Notes</h1>
          <p style={{ color: "#6B7280", fontSize: "14px", margin: "4px 0 0" }}>
            {total} credit note{total !== 1 ? "s" : ""} · AR-03 Gate
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {canRefund && (
            <button
              data-testid="create-refund-btn"
              onClick={() => navigate("/ar/refunds")}
              style={btnOutline}
            >
              Refunds
            </button>
          )}
          {canCreate && (
            <button
              data-testid="create-credit-note-btn"
              onClick={() => setShowCreate(true)}
              style={btnPrimary}
            >
              + Credit Note
            </button>
          )}
        </div>
      </div>

      {/* Status Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "16px", flexWrap: "wrap" }}>
        {STATUS_TABS.map(tab => {
          const cnt = tab.key ? (counts[tab.key] || 0) : total;
          return (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key || "all"}`}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...tabBtn,
                background: activeTab === tab.key ? "#111827" : "#F9FAFB",
                color:      activeTab === tab.key ? "#fff"    : "#374151",
                borderColor: activeTab === tab.key ? "#111827" : "#E5E7EB",
              }}
            >
              {tab.label} {cnt > 0 && <span style={{ marginLeft: "4px", opacity: 0.75 }}>({cnt})</span>}
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div data-testid="cn-load-error" style={errorBox}>{error}</div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div style={{ background: "#fff", borderRadius: "8px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F9FAFB" }}>
                {["Credit Number", "Customer", "Original Invoice", "Credit Amount", "Unapplied", "Date", "Status", ""].map((h, i) => (
                  <th key={i} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {creditNotes.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: "48px", color: "#9CA3AF" }}>
                    No credit notes found
                    {activeTab && ` for status "${activeTab}"`}.
                    {canCreate && " Click \"+ Credit Note\" to create one."}
                  </td>
                </tr>
              ) : creditNotes.map(cn => (
                <tr
                  key={cn.id}
                  data-testid={`cn-row-${cn.id}`}
                  onClick={() => navigate(`/ar/credit-notes/${cn.id}`)}
                  style={{ ...rowStyle, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: "13px" }}>{cn.credit_number}</span></td>
                  <td style={tdStyle}>{cn.customer_name || cn.customer_id}</td>
                  <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: "13px" }}>{cn.original_invoice_number}</span></td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{fmtAmt(cn.credit_amount_minor, cn.currency_code)}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>{fmtAmt(cn.unapplied_amount_minor, cn.currency_code)}</td>
                  <td style={tdStyle}>{fmtDate(cn.credit_date)}</td>
                  <td style={tdStyle}><StatusPill status={cn.status} /></td>
                  <td style={{ ...tdStyle, color: "#6B7280", fontSize: "12px" }}>View →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div style={{ textAlign: "center", padding: "48px", color: "#6B7280" }}>Loading...</div>}

      {/* Create Credit Note Modal */}
      {showCreate && (
        <CreateCreditNoteModal
          onClose={() => setShowCreate(false)}
          onCreated={(cn) => { setShowCreate(false); navigate(`/ar/credit-notes/${cn.id}`); }}
        />
      )}
    </div>
  );
}

// ── Create Credit Note Modal ───────────────────────────────────────────────────

function CreateCreditNoteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    original_invoice_id: "",
    credit_amount_minor: "",
    credit_reason:       "",
    credit_reason_code:  "",
    credit_date:         new Date().toISOString().slice(0, 10),
    accounting_date:     new Date().toISOString().slice(0, 10),
    currency_code:       "INR",
  });
  const [reasonCodes, setReasonCodes] = useState([]);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  useEffect(() => {
    arCreditNoteApi.getMDMConfig("CN_REASON_CODE").then(r => setReasonCodes(r.data.items || []));
  }, []);

  async function lookupInvoice() {
    if (!form.original_invoice_id.trim()) return;
    try {
      const res = await arCreditNoteApi.getInvoiceForCredit(form.original_invoice_id.trim());
      setInvoiceInfo(res.data);
    } catch (e) {
      setInvoiceInfo(null);
      setError("Invoice not found or not ISSUED");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        customer_id:         invoiceInfo?.customer_id || "",
        legal_entity_id:     invoiceInfo?.legal_entity_id || "",
        original_invoice_id: form.original_invoice_id.trim(),
        credit_amount_minor: Math.round(parseFloat(form.credit_amount_minor) * 100),
        credit_reason:       form.credit_reason,
        credit_reason_code:  form.credit_reason_code || null,
        credit_date:         form.credit_date,
        accounting_date:     form.accounting_date,
        currency_code:       form.currency_code,
      };
      const res = await arCreditNoteApi.create(payload);
      onCreated(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to create credit note");
    } finally {
      setLoading(false);
    }
  }

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div style={modalOverlay}>
      <div style={modalBox} data-testid="create-cn-modal">
        <h2 style={{ margin: "0 0 16px", fontSize: "18px", fontWeight: 700 }}>Create Credit Note</h2>

        {error && <div style={errorBox}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={formRow}>
            <label style={labelStyle}>Original Invoice ID</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                data-testid="cn-invoice-id-input"
                style={{ ...inputStyle, flex: 1 }}
                value={form.original_invoice_id}
                onChange={e => f("original_invoice_id", e.target.value)}
                placeholder="Invoice UUID"
                required
              />
              <button type="button" onClick={lookupInvoice} style={btnOutline}>Lookup</button>
            </div>
            {invoiceInfo && (
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "6px", padding: "8px 12px", marginTop: "6px", fontSize: "13px" }}>
                <strong>{invoiceInfo.invoice_number}</strong> · {invoiceInfo.customer_name} · {fmtAmt(invoiceInfo.total_minor, invoiceInfo.currency_code)}
                <br/><span style={{ color: "#6B7280" }}>Eligible to credit: {fmtAmt(invoiceInfo.eligible_credit_minor, invoiceInfo.currency_code)}</span>
              </div>
            )}
          </div>

          <div style={formRow}>
            <label style={labelStyle}>Credit Amount (₹)</label>
            <input data-testid="cn-amount-input" style={inputStyle} type="number" step="0.01" min="0.01" value={form.credit_amount_minor} onChange={e => f("credit_amount_minor", e.target.value)} required />
          </div>

          <div style={formRow}>
            <label style={labelStyle}>Credit Reason</label>
            <input data-testid="cn-reason-input" style={inputStyle} value={form.credit_reason} onChange={e => f("credit_reason", e.target.value)} placeholder="Describe the reason for this credit" required />
          </div>

          <div style={formRow}>
            <label style={labelStyle}>Reason Code</label>
            <select data-testid="cn-reason-code-select" style={inputStyle} value={form.credit_reason_code} onChange={e => f("credit_reason_code", e.target.value)}>
              <option value="">-- Select reason code --</option>
              {reasonCodes.map(rc => <option key={rc.item_code} value={rc.item_code}>{rc.display_name}</option>)}
            </select>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div style={formRow}>
              <label style={labelStyle}>Credit Date</label>
              <input style={inputStyle} type="date" value={form.credit_date} onChange={e => f("credit_date", e.target.value)} required />
            </div>
            <div style={formRow}>
              <label style={labelStyle}>Accounting Date</label>
              <input style={inputStyle} type="date" value={form.accounting_date} onChange={e => f("accounting_date", e.target.value)} required />
            </div>
          </div>

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "16px" }}>
            <button type="button" onClick={onClose} style={btnOutline}>Cancel</button>
            <button data-testid="cn-submit-btn" type="submit" disabled={loading || !invoiceInfo} style={btnPrimary}>
              {loading ? "Creating…" : "Create Credit Note"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const btnPrimary   = { background: "#111827", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer", fontWeight: 600 };
const btnOutline   = { background: "#fff", color: "#374151", border: "1px solid #D1D5DB", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", cursor: "pointer" };
const tabBtn       = { border: "1px solid", borderRadius: "6px", padding: "4px 12px", fontSize: "13px", cursor: "pointer", fontWeight: 500 };
const thStyle      = { padding: "10px 14px", fontSize: "12px", fontWeight: 600, color: "#6B7280", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" };
const tdStyle      = { padding: "12px 14px", fontSize: "14px", color: "#111827", borderTop: "1px solid #F3F4F6" };
const rowStyle     = { transition: "background 0.1s" };
const errorBox     = { background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", borderRadius: "6px", padding: "10px 14px", marginBottom: "12px", fontSize: "14px" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBox     = { background: "#fff", borderRadius: "12px", padding: "24px", width: "560px", maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" };
const formRow      = { marginBottom: "14px" };
const labelStyle   = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inputStyle   = { width: "100%", border: "1px solid #D1D5DB", borderRadius: "6px", padding: "8px 10px", fontSize: "14px", boxSizing: "border-box" };
